import _ from 'lodash';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { createLogger } from 'redux-logger';
import { persistStore } from 'redux-persist';
import createSagaMiddleware from 'redux-saga';
import { all, call, Effect } from 'redux-saga/effects';
import { generateactionDTOWatcher } from 'src/store/action-dto/sagas';
import {
    generateAddCartWatcher,
    generateChangeProductCustomFieldValueInCartWather,
    generateChangeProductQuantityInCartWatcher,
    generateClearShoppingCartWatcher,
    generateOrderAgainWatcher,
    generateOrderingCalcFailureWatcher,
    generateRemoveAllFromCartWatcher,
    generateRemoveFromCartWatcher,
    generateSubmitBookingWatcher,
    generateUpdateShoppingCartWatcher,
} from 'src/store/cart/sagas';
import {
    generateFetchCommissionEntriesWatcher,
    generateFetchCommissionStatsWatcher,
    generateFetchMyCommissionStatsWatcher,
} from 'src/store/comission-account/sagas';
import {
    generateCreateEntityWatcher,
    generateDeleteEntityWatcher,
    generateFetchWatcherByEntity,
    generateUpdateEntityWatcher,
} from 'src/store/entities/sagas';
import { EntityDescriptions } from 'src/store/entities/types';
import EntityDescription from 'src/store/entities/types/EntityDescription';
import { generateFetchEventsWatcher } from 'src/store/event/sagas';
import { registerPersistor } from 'src/store/persistor';
import { createPersistedRootReducer } from 'src/store/reducer';
import { generateFetchShippingCostTableWatcher } from 'src/store/shipping-cost-table/sagas';
import {
    generateFetchDirectCustomersWatcher,
    generateFetchSuccessListEntryHistoryWatcher,
    generateFetchSuccessListOverviewWatcher,
    generateFetchSuccessListRegionsWatcher,
    generateFetchSuccessListTopicsWatcher,
    generateFetchSuccessListWatcher,
} from 'src/store/success-list/sagas';
import {
    generateCreateNewVideoAdminWatcher,
    generateEditVideoAdminWatcher,
    generateFetchAllVideoCategoriesAdminWatcher,
    generateFetchVideoCategoriesByTopicIdAdminWatcher,
    generateFetchVideoRatingsAdminWatcher,
    generateFetchVideosByCategoryAdminWatcher,
    generateFetchVideosWithoutCategoryAdminWatcher,
    generateFetchVideoTopicsAdminWatcher,
} from 'src/store/video-admin/sagas';
import {
    generateFetchNextVideoWatcher,
    generateFetchVideoCategoriesWatcher,
    generateFetchVideoLanguagesWatcher,
    generateFetchVideoTopicsWatcher,
} from 'src/store/video/sagas';
import { generateFetchLandingpageWatcher } from 'src/store/welcome/sagas';
import {
    generateFetchAboSubscriptionOrderProductWatcher,
    generateFetchAboTemplateByIdWatcher,
    generateFetchAboTemplatesByCustomerIdWatcher,
    generateFetchAboTemplatesByFilterWatcher,
    generateInsertAboSubscriptionWatcher,
    generateUpdateAboSubscriptionWatcher,
} from './abo-templates/sagas';
import { generateFetchAddressConfirmationsWatcher } from './address-confirmation/sagas';
import {
    generateClearCommissionInvoiceDetailsWatcher,
    generateCreateNewCommissionInvoiceFilterWatcher,
    generateDeleteCommissionInvoiceFilterWatcher,
    generateFetchCommissionInvoiceDetailsPdfWatcher,
    generateFetchCommissionInvoiceDetailsWatcher,
    generateFetchCommissionInvoiceFilterWatcher,
    generateFetchCommissionInvoicePdfWatcher,
    generateFetchCommissionInvoicesByFilterIdWatcher,
    generateFetchCommissionInvoicesByFilterWatcher,
    generateUpdateCommissionInvoiceFilterWatcher,
} from './admin-commission-invoice/sagas';
import { generateFetchAllLandingpagesWatcher } from './admin-landingPage/sagas';
import {
    generateClearOrderProductDetailsWatcher,
    generateFetchOrderFilterWatcher,
    generateFetchOrderProductDetailsWatcher,
    generateFetchOrdersByFilterIdWatcher,
    generateFetchOrdersByFilterWatcher,
    generateUpdateBillingAddressOrderWatcher,
    generateUpdateDeliveryAddressOrderWatcher,
    generateUpdateOrderWatcher,
} from './admin-orders/sagas';
import { generateFetchServiceComplaintsWatcher } from './admin-service/sagas';
import {
    generateConfirmAdyenBatchWatcher,
    generateFetchAdyenBatchesByBatchNumberWatcher,
    generateFetchAdyenBatchesWatcher,
} from './adyen-batch/sagas';
import {
    generateCreateNewArticleCountryTaxWatcher,
    generateCreateNewGroupRebateWatcher,
    generateDeleteArticleCountryTaxWatcher,
    generateDeleteGroupRebateWatcher,
    generateFetchArticleCountryTaxesWatcher,
    generateFetchArticleGroupWatcher,
    generateFetchGroupRebatesWatcher,
    generateUpdateArticleCountryTaxWatcher,
    generateUpdateGroupRebateWatcher,
} from './article-management/article-group/sagas';
import {
    generateCreateArticleProductGroupWatcher,
    generateDeactivateArticleArticleWatcher,
    generateDeleteArticleArticleAssignmentWatcher,
    generateDeleteArticleImageWatcher,
    generateDeleteArticleProductGroupWatcher,
    generateFetchArticleArticleAssignmentWatcher,
    generateFetchArticleArticleProductGroupWatcher,
    generateFetchArticleArticleWatcher,
    generateFetchArticleCustomFieldWatcher,
    generateFetchArticleImageWatcher,
    generateFetchArticlePropertyWatcher,
    generateUpdateArticleArticleWatcher,
    generateUpdateArticleImageWatcher,
    generateUpdateArticleProductGroupWatcher,
    generateUploadArticleImageWatcher,
} from './article-management/article/sagas';
import { generateFetchArticleCommissionModelWatcher } from './article-management/commission-model/sagas';
import { generateFetchAdminProductGroupWatcher } from './article-management/productgroup/sagas';
import {
    generateCreateVendorArticlePriceWatcher,
    generateCreateVendorArticleWatcher,
    generateDeleteVendorArticlePriceWatcher,
    generateDeleteVendorArticleWatcher,
    generateFetchVendorArticlePricesWatcher,
    generateFetchVendorArticlesWatcher,
    generateUpdateVendorArticlePriceWatcher,
    generateUpdateVendorArticleWatcher,
} from './article-management/vendor-article/sagas';
import { generateFetchConfirmationsWatcher } from './confirmations/sagas';
import { generateFetchCountriesWatcher } from './country/sagas';
import { generateFetchCustomerAddressesWatcher } from './customer/addresses/sagas';
import {
    generateFetchCustomerDataWatcher,
    generateFetchCustomerFeedbackWatcher,
    generateFetchCustomerMailAddressesWatcher,
    generateFetchCustomerPhoneNumbersWatcher,
} from './customer/data/sagas';
import { generateFetchCustomerDocumentsWatcher } from './customer/documents/sagas';
import {
    generateFetchCustomerBillsInfoWatcher,
    generateFetchCustomerBillsWatcher,
    generateFetchCustomerCommissionBillsInfoWatcher,
    generateFetchCustomerCommissionBillsWatcher,
    generateFetchCustomerDownloadsWatcher,
    generateFetchCustomerDownloadTopicsWatcher,
} from './customer/downloads/sagas';
import { generateFetchCustomerMessagesWatcher } from './customer/notes/sagas';
import { generateFetchCustomerOrdersWatcher } from './customer/orders/sagas';
import { generateFetchPartnerNoPayoutRationalesWatcher } from './customer/partner-data/sagas';
import {
    generateAddCustomerPaymentMethodsWatcher,
    generateDeactivateCustomerPaymentMethodsWatcher,
    generateFetchCustomerPaymentMethodsAdminWatcher,
    generateFetchCustomerPaymentMethodsWatcher,
} from './customer/paymentMethods/sagas';
import {
    generateFetchCustomerAppliancesWatcher,
    generateFetchCustomerComplaintsWatcher,
} from './customer/services/sagas';
import {
    generateCreateDownloadCategoryWatcher,
    generateCreateDownloadLanguageWatcher,
    generateCreateDownloadWatcher,
    generateDeleteDownloadCategoryWatcher,
    generateDeleteDownloadLanguageWatcher,
    generateDeleteDownloadWatcher,
    generateFetchDownloadCategoriesWatcher,
    generateFetchDownloadLanguagesWatcher,
    generateFetchDownloadsWatcher,
    generateUpdateDownloadWatcher,
} from './download-admin/sagas';
import {
    generateFetchCreateNewEventCommissionModelWatcher,
    generateFetchCreateNewEventLocationWatcher,
    generateFetchCreateNewEventParticipantWatcher,
    generateFetchCreateNewEventTypeWatcher,
    generateFetchCreateNewEventWatcher,
    generateFetchDeactivateEventLanguageCountryModelWatcher,
    generateFetchDeactivateEventTypeWatcher,
    generateFetchDeleteEventCommissionModelWatcher,
    generateFetchDeleteEventParticipantWatcher,
    generateFetchDeleteEventWatcher,
    generateFetchEventCommissionModelsWatcher,
    generateFetchEventEventsWatcher,
    generateFetchEventLanguageCountryModelWatcher,
    generateFetchEventLocationsWatcher,
    generateFetchEventParticipantsWatcher,
    generateFetchEventTypesWatcher,
    generateFetchUpdateEventCommissionModelWatcher,
    generateFetchUpdateEventLocationWatcher,
    generateFetchUpdateEventParticipantWatcher,
    generateFetchUpdateEventTypeWatcher,
    generateFetchUpdateEventWatcher,
} from './event-admin/sagas';
import { generateFetchExpertise, generateSendRepairOffer } from './expertise/sagas';
import { generateFetchLandingpagesWatcher } from './landingpage/sagas';
import {
    generateAddNewMailscopeForMailKeyWatcher,
    generateDeleteMailscopeWatcher,
    generateFetchMailTemplatesWatcher,
    generateFetchPreviewOfMailTemplateWatcher,
    generateSendMailPreviewToReceiverWatcher,
} from './mail-admin/sagas';
import {
    generateConfirmMollieBatchWatcher,
    generateFetchMollieBatchesWatcher,
    generateFetchMollieSettlementOrdersWatcher,
} from './mollie-batch/sagas';
import { generateFetchNoPayoutRationalesWatcher } from './no-payout-rationale/sagas';
import {
    generateApplyOfferWatcher,
    generateClearMyOffersWatcher,
    generateEditOfferWatcher,
    generateFetchMyOffersWatcher,
    generateFetchOffersWatcher,
    generateRestoreParkedCartWatcher,
    generateSubmitAndPayOfferWatcher,
    generateSubmitOfferWatcher,
    generateUpdateOfferWatcher,
} from './offer/sagas';
import { generateFetchOrderProductsRepair } from './order-products/sagas';
import { generateFetchOrderingNoteSubjectsWatcher } from './ordering-note-subject/sagas';
import {
    generateFetchOrganizationAppointmentsWatcher,
    generateFetchOrganizationContactsWatcher,
    generateFetchOrganizationCustomerAddressesWatcher,
    generateFetchOrganizationCustomerAppliancesWatcher,
    generateFetchOrganizationCustomerDataWatcher,
    generateFetchOrganizationCustomerEnablesWatcher,
    generateFetchOrganizationCustomerHistoryWatcher,
    generateFetchOrganizationCustomerLandingpagesWatcher,
    generateFetchOrganizationCustomerOrdersWatcher,
    generateFetchOrganizationCustomerReferralPartnerWatcher,
    generateFetchOrganizationCustomerSubscriptionsWatcher,
    generateFetchOrganizationCustomersWatcher,
    generateFetchOrganizationFavoritesWatcher,
    generateFetchOrganizationLeadUnlockWatcher,
    generateFetchOrganizationLicensorsWatcher,
    generateFetchOrganizationListCountsWatcher,
    generateFetchOrganizationOffersWatcher,
    generateFetchOrganizationOfficeProfisWatcher,
    generateFetchOrganizationOrdersWatcher,
    generateFetchOrganizationOverviewWatcher,
    generateFetchOrganizationPlacementsWatcher,
    generateFetchOrganizationRankingWatcher,
    generateFetchOrganizationSisPartnersWatcher,
    generateFetchOrganizationStatisticWatcher,
    generateFetchOrganizationTodoCountWatcher,
    generateFetchOrganizationTodosWatcher,
    generatefetchRegisteredEventsWatcher,
    generatefetchVideoListWatcher,
} from './organization/sagas';
import {
    generateCreateNewPaymentMethod,
    generateFetchAllPaymentMethods,
    generateUpdatePaymentMethod,
} from './payment-method-admin/sagas';
import {
    generateAssignBatchFileToOrderWatcher,
    generateFetchPaypalBatchesWatcher,
    generateFetchPaypalBatchFileDetailsWatcher,
    generateFetchPaypalUnassignedOrdersWatcher,
} from './paypal-batch/sagas';
import { generateFetchProductGroups } from './product-groups/sagas';
import { generateFetchProducts } from './products/sagas';
import { generateFetchRepairManagement } from './repair-management/sagas';
import { generateFetchRepairOrder, generateFinishRepairOrder, generateRepairOrderFailure } from './repair-orders/sagas';
import { generateFetchAllRoles } from './roles/sagas';
import {
    generateConfirmSepaBatchWatcher,
    generateFetchSepaBatchesWatcher,
    generateFetchSepaBatchPreviewWatcher,
} from './sepa-batch/sagas';
import { generateFetchSepaPaymentDetailsWatcher, generateFetchSepaPaymentsWatcher } from './sepa/sagas';
import {
    generateCreateLanguageBlock,
    generateEditLanguageBlockTranslation,
    generateFetchAllLanguageBlocksWatcher,
    generateFetchAllRegionBlocksWatcher,
    generateFetchDocumentRegionsByTemplateKeyWatcher,
    generateFetchLanguageBlocksByQueryWatcher,
    generateFetchLanguageBlocksByTemplateKeyWatcher,
    generateFetchLanguageTranslationsByTemplateKeyWatcher,
    generateFetchTextBlockLanguagesWatcher,
} from './templateAdmin/sagas';
import { generateFetchUsers } from './users/sagas';

// create the saga middleware
const sagaMiddleware = createSagaMiddleware();

// configure store with persistence and middleware
export default function configureStore() {
    const reducer = createPersistedRootReducer();

    const store = process.env.NODE_ENV === 'production' ? configureProdStore(reducer) : configureDevStore(reducer);
    const persistor = persistStore(store);
    registerPersistor(persistor);

    sagaMiddleware.run(combineWatchers);
    return { store, persistor };
}

export function* combineWatchers() {
    const watchers: Effect[] = [
        call(generateFetchAddressConfirmationsWatcher),

        /* Article Management */
        call(generateFetchArticleArticleWatcher),
        call(generateUpdateArticleArticleWatcher),
        call(generateDeactivateArticleArticleWatcher),

        call(generateFetchArticleArticleAssignmentWatcher),
        call(generateDeleteArticleArticleAssignmentWatcher),

        call(generateFetchArticleCustomFieldWatcher),
        call(generateFetchArticlePropertyWatcher),
        call(generateFetchArticleCommissionModelWatcher),
        call(generateFetchArticleGroupWatcher),

        /* Article - Image */
        call(generateFetchArticleImageWatcher),
        call(generateUploadArticleImageWatcher),
        call(generateUpdateArticleImageWatcher),
        call(generateDeleteArticleImageWatcher),
        /* Article - Country Tax */
        call(generateFetchArticleCountryTaxesWatcher),
        call(generateCreateNewArticleCountryTaxWatcher),
        call(generateUpdateArticleCountryTaxWatcher),
        call(generateDeleteArticleCountryTaxWatcher),
        /* Article - Group Rebate */
        call(generateFetchGroupRebatesWatcher),
        call(generateCreateNewGroupRebateWatcher),
        call(generateUpdateGroupRebateWatcher),
        call(generateDeleteGroupRebateWatcher),
        /* Article - Article ProductGroup */
        call(generateFetchArticleArticleProductGroupWatcher),
        call(generateCreateArticleProductGroupWatcher),
        call(generateUpdateArticleProductGroupWatcher),
        call(generateDeleteArticleProductGroupWatcher),
        /* Admin Product Group */
        call(generateFetchAdminProductGroupWatcher),
        /* Vendor Article */
        call(generateFetchVendorArticlesWatcher),
        call(generateCreateVendorArticleWatcher),
        call(generateUpdateVendorArticleWatcher),
        call(generateDeleteVendorArticleWatcher),
        /* Vendor Article Price */
        call(generateFetchVendorArticlePricesWatcher),
        call(generateCreateVendorArticlePriceWatcher),
        call(generateUpdateVendorArticlePriceWatcher),
        call(generateDeleteVendorArticlePriceWatcher),

        /* Event Event */
        call(generateFetchEventEventsWatcher),
        call(generateFetchCreateNewEventWatcher),
        call(generateFetchUpdateEventWatcher),
        call(generateFetchDeleteEventWatcher),
        /* Event Commission Model */
        call(generateFetchEventCommissionModelsWatcher),
        call(generateFetchCreateNewEventCommissionModelWatcher),
        call(generateFetchUpdateEventCommissionModelWatcher),
        call(generateFetchDeleteEventCommissionModelWatcher),
        /* Event Location */
        call(generateFetchEventLocationsWatcher),
        call(generateFetchCreateNewEventLocationWatcher),
        call(generateFetchUpdateEventLocationWatcher),
        /* Event Language Country Model */
        call(generateFetchEventLanguageCountryModelWatcher),
        call(generateFetchDeactivateEventLanguageCountryModelWatcher),
        /* Event Participant */
        call(generateFetchEventParticipantsWatcher),
        call(generateFetchCreateNewEventParticipantWatcher),
        call(generateFetchUpdateEventParticipantWatcher),
        call(generateFetchDeleteEventParticipantWatcher),
        /* Event Type */
        call(generateFetchEventTypesWatcher),
        call(generateFetchCreateNewEventTypeWatcher),
        call(generateFetchUpdateEventTypeWatcher),
        call(generateFetchDeactivateEventTypeWatcher),

        /* Confirmation */
        call(generateFetchConfirmationsWatcher),

        /* Countries */
        call(generateFetchCountriesWatcher),

        call(generateAddCartWatcher),
        call(generateApplyOfferWatcher),
        call(generateRestoreParkedCartWatcher),
        call(generateRemoveFromCartWatcher),
        call(generateRemoveAllFromCartWatcher),
        call(generateChangeProductQuantityInCartWatcher),
        call(generateChangeProductCustomFieldValueInCartWather),
        call(generateUpdateShoppingCartWatcher),
        call(generateClearShoppingCartWatcher),
        call(generateSubmitBookingWatcher),
        call(generateEditOfferWatcher),
        call(generateUpdateOfferWatcher),
        call(generateSubmitOfferWatcher),
        call(generateSubmitAndPayOfferWatcher),
        call(generateOrderingCalcFailureWatcher),
        call(generateOrderAgainWatcher),

        /* Customer Data */
        call(generateFetchCustomerMailAddressesWatcher),
        call(generateFetchCustomerPhoneNumbersWatcher),
        call(generateFetchCustomerAddressesWatcher),
        call(generateFetchCustomerPaymentMethodsWatcher),
        call(generateFetchCustomerDataWatcher),
        call(generateFetchCustomerMessagesWatcher),
        call(generateFetchCustomerDocumentsWatcher),
        call(generateFetchCustomerOrdersWatcher),
        call(generateFetchCustomerAppliancesWatcher),
        call(generateFetchCustomerComplaintsWatcher),
        call(generateFetchCustomerFeedbackWatcher),
        call(generateAddCustomerPaymentMethodsWatcher),
        call(generateDeactivateCustomerPaymentMethodsWatcher),
        call(generateFetchCustomerPaymentMethodsAdminWatcher),

        /* Office - LandingPage Admin */
        call(generateFetchAllLandingpagesWatcher),

        /* Office - Abo Templates */
        call(generateFetchAboTemplatesByCustomerIdWatcher),
        call(generateFetchAboTemplatesByFilterWatcher),
        call(generateFetchAboTemplateByIdWatcher),
        call(generateFetchAboSubscriptionOrderProductWatcher),
        call(generateInsertAboSubscriptionWatcher),
        call(generateUpdateAboSubscriptionWatcher),

        /* Offsetting - Adyen */
        call(generateFetchAdyenBatchesWatcher),
        call(generateFetchAdyenBatchesByBatchNumberWatcher),
        call(generateConfirmAdyenBatchWatcher),

        /* Offsetting - Sepa Payments */
        call(generateFetchSepaPaymentsWatcher),
        call(generateFetchSepaPaymentDetailsWatcher),

        /* Offsetting - Mollie Batch */
        call(generateFetchMollieBatchesWatcher),
        call(generateFetchMollieSettlementOrdersWatcher),
        call(generateConfirmMollieBatchWatcher),

        /* Offsetting - Sepa Batch */
        call(generateFetchSepaBatchesWatcher),
        call(generateFetchSepaBatchPreviewWatcher),
        call(generateConfirmSepaBatchWatcher),

        /* Offsetting - PayPal */
        call(generateFetchPaypalBatchesWatcher),
        call(generateFetchPaypalBatchFileDetailsWatcher),
        call(generateFetchPaypalUnassignedOrdersWatcher),
        call(generateAssignBatchFileToOrderWatcher),

        /* Offsetting - Commission Invoice */
        call(generateFetchCommissionInvoiceFilterWatcher),
        call(generateFetchCommissionInvoicesByFilterWatcher),
        call(generateFetchCommissionInvoicesByFilterIdWatcher),
        call(generateFetchCommissionInvoicePdfWatcher),
        call(generateFetchCommissionInvoiceDetailsPdfWatcher),
        call(generateCreateNewCommissionInvoiceFilterWatcher),
        call(generateUpdateCommissionInvoiceFilterWatcher),
        call(generateDeleteCommissionInvoiceFilterWatcher),
        call(generateFetchCommissionInvoiceDetailsWatcher),
        call(generateClearCommissionInvoiceDetailsWatcher),

        /* Offsetting - Orders */
        call(generateFetchOrderFilterWatcher),
        call(generateFetchOrdersByFilterWatcher),
        call(generateFetchOrdersByFilterIdWatcher),
        call(generateFetchOrderProductDetailsWatcher),
        call(generateClearOrderProductDetailsWatcher),
        call(generateUpdateOrderWatcher),
        call(generateUpdateBillingAddressOrderWatcher),
        call(generateUpdateDeliveryAddressOrderWatcher),

        /* Repair Order */
        call(generateFetchExpertise),
        call(generateSendRepairOffer),
        call(generateFetchRepairOrder),
        call(generateFinishRepairOrder),
        call(generateRepairOrderFailure),

        call(generateFetchOrderProductsRepair),

        call(generateFetchRepairManagement),

        /* Service Complaints */
        call(generateFetchServiceComplaintsWatcher),

        /* IT - Mail Management */
        call(generateFetchMailTemplatesWatcher),
        call(generateAddNewMailscopeForMailKeyWatcher),
        call(generateDeleteMailscopeWatcher),
        call(generateFetchPreviewOfMailTemplateWatcher),
        call(generateSendMailPreviewToReceiverWatcher),

        call(generateFetchShippingCostTableWatcher),
        call(generateactionDTOWatcher),
        call(generateFetchCustomerDownloadTopicsWatcher),
        call(generateFetchCustomerDownloadsWatcher),
        call(generateFetchCustomerBillsInfoWatcher),
        call(generateFetchCustomerBillsWatcher),
        call(generateFetchCustomerCommissionBillsInfoWatcher),
        call(generateFetchCustomerCommissionBillsWatcher),

        call(generateFetchProducts),
        call(generateFetchProductGroups),

        call(generateFetchAllRoles),

        call(generateFetchUsers),

        /* IT - Videos */
        call(generateFetchVideoTopicsWatcher),
        call(generateFetchVideoCategoriesWatcher),
        call(generateFetchVideoLanguagesWatcher),
        call(generateFetchAllVideoCategoriesAdminWatcher),
        call(generateFetchVideoCategoriesByTopicIdAdminWatcher),
        call(generateFetchVideoTopicsAdminWatcher),
        call(generateFetchVideosByCategoryAdminWatcher),
        call(generateFetchVideosWithoutCategoryAdminWatcher),
        call(generateCreateNewVideoAdminWatcher),
        call(generateEditVideoAdminWatcher),
        call(generateFetchVideoRatingsAdminWatcher),

        /* IT - No Payout Rationales */
        call(generateFetchNoPayoutRationalesWatcher),
        call(generateFetchPartnerNoPayoutRationalesWatcher),

        /* IT - Ordering Note Subjects */
        call(generateFetchOrderingNoteSubjectsWatcher),

        call(generateFetchEventsWatcher),
        call(generateFetchSuccessListOverviewWatcher),
        call(generateFetchSuccessListTopicsWatcher),
        call(generateFetchSuccessListWatcher),
        call(generateFetchSuccessListEntryHistoryWatcher),
        call(generateFetchDirectCustomersWatcher),
        call(generateFetchSuccessListRegionsWatcher),
        call(generateFetchCommissionStatsWatcher),
        call(generateFetchMyCommissionStatsWatcher),
        call(generateFetchCommissionEntriesWatcher),
        call(generateFetchOrganizationStatisticWatcher),
        call(generateFetchOrganizationLeadUnlockWatcher),
        call(generateFetchOrganizationRankingWatcher),
        call(generateFetchOrganizationAppointmentsWatcher),
        call(generateFetchOrganizationOffersWatcher),
        call(generateFetchOrganizationOrdersWatcher),
        call(generateFetchOrganizationOverviewWatcher),
        call(generateFetchOrganizationContactsWatcher),
        call(generateFetchOrganizationTodosWatcher),
        call(generateFetchOrganizationTodoCountWatcher),
        call(generateFetchOrganizationListCountsWatcher),
        call(generateFetchOrganizationCustomersWatcher),
        call(generateFetchOrganizationSisPartnersWatcher),
        call(generateFetchOrganizationLicensorsWatcher),
        call(generateFetchOrganizationPlacementsWatcher),
        call(generateFetchOrganizationOfficeProfisWatcher),
        call(generatefetchRegisteredEventsWatcher),
        call(generateFetchOrganizationCustomerAppliancesWatcher),
        call(generatefetchVideoListWatcher),
        call(generateFetchNextVideoWatcher),
        call(generateFetchOrganizationCustomerOrdersWatcher),
        call(generateFetchOrganizationCustomerSubscriptionsWatcher),
        call(generateFetchOrganizationCustomerHistoryWatcher),
        call(generateFetchOrganizationCustomerDataWatcher),
        call(generateFetchOrganizationCustomerAddressesWatcher),
        call(generateFetchOrganizationCustomerLandingpagesWatcher),
        call(generateFetchOrganizationCustomerReferralPartnerWatcher),
        call(generateFetchOrganizationCustomerEnablesWatcher),
        call(generateFetchOrganizationFavoritesWatcher),

        /* Template */
        call(generateCreateLanguageBlock),
        call(generateEditLanguageBlockTranslation),
        call(generateFetchAllLanguageBlocksWatcher),
        call(generateFetchLanguageBlocksByQueryWatcher),
        call(generateFetchLanguageBlocksByTemplateKeyWatcher),
        call(generateFetchLanguageTranslationsByTemplateKeyWatcher),
        call(generateFetchTextBlockLanguagesWatcher),
        call(generateFetchAllRegionBlocksWatcher),
        call(generateFetchDocumentRegionsByTemplateKeyWatcher),

        /* Download */
        call(generateFetchDownloadCategoriesWatcher),
        call(generateCreateDownloadCategoryWatcher),
        call(generateDeleteDownloadCategoryWatcher),
        call(generateFetchDownloadsWatcher),
        call(generateCreateDownloadWatcher),
        call(generateUpdateDownloadWatcher),
        call(generateDeleteDownloadWatcher),
        call(generateFetchDownloadLanguagesWatcher),
        call(generateCreateDownloadLanguageWatcher),
        call(generateDeleteDownloadLanguageWatcher),

        call(generateFetchLandingpageWatcher),
        call(generateFetchLandingpagesWatcher),
        call(generateFetchMyOffersWatcher),
        call(generateFetchOffersWatcher),
        call(generateClearMyOffersWatcher),
        call(generateFetchAboTemplatesByCustomerIdWatcher),

        /* PaymentMethod */
        call(generateFetchAllPaymentMethods),
        call(generateCreateNewPaymentMethod),
        call(generateUpdatePaymentMethod),
    ];

    _.forEach(EntityDescriptions, (entityDescription: EntityDescription) => {
        watchers.push(call(generateFetchWatcherByEntity, entityDescription));
        watchers.push(call(generateCreateEntityWatcher, entityDescription));
        watchers.push(call(generateUpdateEntityWatcher, entityDescription));
        watchers.push(call(generateDeleteEntityWatcher, entityDescription));
    });

    yield all(watchers);
}

const configureProdStore = (reducer: any) => {
    console.log('configure production store');
    return createStore(reducer, applyMiddleware(sagaMiddleware));
};
const configureDevStore = (reducer: any) => {
    console.log('configure development store');

    const logger = createLogger({
        collapsed: true,
        diff: true,
        duration: true,
    });

    return createStore(reducer, composeWithDevTools(applyMiddleware(sagaMiddleware, logger)));
};
